import axios from 'axios';
import type { AxiosError, InternalAxiosRequestConfig } from 'axios';
import {
  Configuration,
  AdminControllerApi,
  ApprovalEndpointsApi,
  AuthControllerApi,
  BusinessEntityControllerApi,
  BusinessInvitationsApi,
  CampaignEndpointsApi,
  CampaignOutdoorEndpointsApi,
  EmailsControllerApi,
  EventsEndpointsApi,
  InvoicesEndpointsApi,
  MessagesEndpointsApi,
  OfflineCampaignEndpointsApi,
  OutdoorControllerApi,
  PaymentsEndpointsApi,
  SectionViewsStatesApi,
  UserControllerApi,
} from '../api/generated';
import { useAuthStore } from '../store/authStore';
import { decodeJwt } from '../utils/format';
import { readTiffInfo, sniffCreativeFormat } from './creative-format';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

export const apiClient = axios.create({ baseURL: API_BASE_URL });

// Plain instance with no interceptors - used only for token refresh calls so that
// (a) the expired access token is never forwarded as an Authorization header, and
// (b) the interceptors below cannot trigger themselves recursively.
const authAxios = axios.create({ baseURL: API_BASE_URL });

// --- Shared refresh logic -------------------------------------------------

let refreshPromise: Promise<string> | null = null;

const refreshTokens = (): Promise<string> => {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const storedRefresh = localStorage.getItem('refreshToken');
      if (!storedRefresh) throw new Error('no_refresh_token');

      const { data } = await authAxios.post<{ accessToken?: string; refreshToken?: string }>(
        '/api/v1/auth/refresh',
        { refreshToken: storedRefresh },
      );
      if (!data.accessToken) throw new Error('empty_access_token');

      localStorage.setItem('accessToken', data.accessToken);
      if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
      useAuthStore.getState().updateTokens({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
      });

      return data.accessToken;
    })().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

const handleAuthFailure = () => {
  useAuthStore.getState().logout();
  window.location.replace('/login');
};

// --- Request interceptor: attach token; refresh proactively if expired ----

apiClient.interceptors.request.use(async (config) => {
  let token = localStorage.getItem('accessToken');

  // No cached access token - e.g. it was just cleared elsewhere (business/account context
  // just changed) to force a refresh on the next call. Without this, the request would go
  // out with no Authorization header at all and come back anonymous/empty instead of
  // waiting the (usually sub-second) moment it takes to mint a fresh token.
  if (!token) {
    if (!localStorage.getItem('refreshToken')) return config;
    try {
      token = await refreshTokens();
    } catch {
      handleAuthFailure();
      return Promise.reject(new Error('authentication_failed'));
    }
  }

  const payload = decodeJwt(token) as { exp?: number } | null;
  const isExpired = payload?.exp != null && payload.exp * 1000 < Date.now();

  if (isExpired) {
    try {
      const newToken = await refreshTokens();
      config.headers.Authorization = `Bearer ${newToken}`;
    } catch {
      handleAuthFailure();
      return Promise.reject(new Error('authentication_failed'));
    }
  } else {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

// --- Response interceptor: handle unexpected 401s (clock skew, revocation) -

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;

    if (!original || error.response?.status !== 401 || original._retry) {
      return Promise.reject(error);
    }

    try {
      const newToken = await refreshTokens();
      original._retry = true;
      original.headers.Authorization = `Bearer ${newToken}`;
      return apiClient(original);
    } catch {
      handleAuthFailure();
      return new Promise(() => {});
    }
  },
);

// Bare instance with no baseURL, no auth interceptors - used exclusively
// for Azure Blob Storage SAS URL uploads where the auth is embedded in the URL.
const blobAxios = axios.create();

// -------------------------------------------------------------------------

const cfg = new Configuration({ basePath: API_BASE_URL });

export const adminApi = new AdminControllerApi(cfg, undefined, apiClient);
export const approvalApi = new ApprovalEndpointsApi(cfg, undefined, apiClient);
export const authApi = new AuthControllerApi(cfg, undefined, apiClient);
export const emailsApi = new EmailsControllerApi(cfg, undefined, apiClient);
export const businessApi = new BusinessEntityControllerApi(cfg, undefined, apiClient);
export const invitationsApi = new BusinessInvitationsApi(cfg, undefined, apiClient);
export const campaignApi = new CampaignEndpointsApi(cfg, undefined, apiClient);
export const campaignOutdoorApi = new CampaignOutdoorEndpointsApi(cfg, undefined, apiClient);
export const eventsApi = new EventsEndpointsApi(cfg, undefined, apiClient);
export const invoicesApi = new InvoicesEndpointsApi(cfg, undefined, apiClient);
export const messagesApi = new MessagesEndpointsApi(cfg, undefined, apiClient);
export const offlineCampaignApi = new OfflineCampaignEndpointsApi(cfg, undefined, apiClient);
export const outdoorApi = new OutdoorControllerApi(cfg, undefined, apiClient);
export const paymentsApi = new PaymentsEndpointsApi(cfg, undefined, apiClient);
export const sectionViewsApi = new SectionViewsStatesApi(cfg, undefined, apiClient);
export const userApi = new UserControllerApi(cfg, undefined, apiClient);

export const getImageDimensions = async (file: File): Promise<{ width: number; height: number } | null> => {
  // Most browsers can't decode TIFF (an accepted creative format), but its header has the size.
  if ((await sniffCreativeFormat(file)) === 'tiff') {
    const tiff = await readTiffInfo(file);
    return tiff ? { width: tiff.width, height: tiff.height } : null;
  }
  if (!file.type.startsWith('image/')) return null;
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
};

export const uploadFileToBlobStorage = async (
  sasUrl: string,
  file: File,
  metadata?: Record<string, string>,
): Promise<void> => {
  const dimensions = await getImageDimensions(file);
  const { userUuid } = useAuthStore.getState();
  const extraMeta = metadata
    ? Object.fromEntries(Object.entries(metadata).map(([k, v]) => [`x-ms-meta-${k}`, v]))
    : {};
  await blobAxios.put(sasUrl, file, {
    headers: {
      'x-ms-blob-type': 'BlockBlob',
      'Content-Type': file.type || 'application/octet-stream',
      ...(dimensions && {
        'x-ms-meta-width': String(dimensions.width),
        'x-ms-meta-height': String(dimensions.height),
      }),
      ...(userUuid && {
        'x-ms-meta-userid': userUuid,
      }),
      ...extraMeta,
    },
  });
};
